import React, { useMemo } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { ArrowLeftIcon, AcademicCapIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../interaxDesignSystemComponents'
import { PageHeader } from '../../../shared/components/PageHeader'
import VideoCard from '../../../components/Videos/VideoCard'
import { useTutorialVideos } from '../../../apiLayer'
import type { TutorialVideo } from '../../../../../pure-js/types/TutorialVideo'
import { Language } from '../../../../../pure-js/types/ExpertVideo'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageWrapper } from '../../../shared/components'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { filterVideosByActive, filterVideosByLanguage } from '../../../shared/utils/videoFilters'

/**
 * Tutorial Videos page component for gamers
 * Displays tutorial videos from backend with client-side language filtering and embedded playback
 */
export const TutorialVideosPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  // Get current language (en or sv), default to 'en'
  const currentLanguage = useMemo<Language>(() => {
    const lang = i18n.language || 'en'
    return lang === 'sv' ? 'sv' : 'en'
  }, [i18n.language])

  // Fetch all videos from backend (no language filtering)
  const { data: videosResponse, isLoading, error, isError } = useTutorialVideos()

  // Filter videos: active only → language
  const videos = useMemo(() => {
    const allVideos = videosResponse?.data || []
    const activeOnly = filterVideosByActive(allVideos)
    return filterVideosByLanguage(activeOnly, currentLanguage)
  }, [videosResponse?.data, currentLanguage])

  const handleBackToFaq = () => {
    navigate('/gamer/faq')
  }

  return (
    <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Back Button - Top Left */}
        <Box>
          <Button
            variant="secondary"
            onClick={handleBackToFaq}
            icon={<ArrowLeftIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            {t('tutorialVideos.backToFaq') || t('videos.backToFaq') || 'Back to FAQ'}
          </Button>
        </Box>

        {/* Header Section */}
        <PageHeader
          badgeText="TUTORIAL VIDEOS"
          badgeIcon={<AcademicCapIcon />}
          title={t('tutorialVideos.title') || 'Tutorial Videos'}
          subtitle={t('tutorialVideos.subtitle') || 'Learn through step-by-step tutorials'}
        />

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <Spinner
              size="large"
              color="sun"
              text={t('tutorialVideos.loading') || t('videos.loading') || 'Loading videos...'}
            />
          </Box>
        )}

        {/* Error State */}
        {(error || isError) && !isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              gap: 2
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.redError, textAlign: 'center', mb: 2 }}>
              {t('tutorialVideos.error') || t('videos.error') || 'Error loading videos. Please try again.'}
            </Typography>
            <Typography sx={{ color: INTERAX_COLORS.thunderGrey, textAlign: 'center', fontSize: '14px' }}>
              {error instanceof Error ? error.message : String(error || 'Unknown error')}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !error && !isError && videos.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.thunderGrey, textAlign: 'center' }}>
              {t('tutorialVideos.empty') || 'No tutorial videos available yet.'}
            </Typography>
          </Box>
        )}

        {/* Videos Grid */}
        {!isLoading && !error && !isError && videos.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
            {videos.map((video: TutorialVideo) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </Box>
        )}
      </Box>
    </PageWrapper>
  )
}
